import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Theme,
  Typography,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import React, { useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { MenuHeaderTemplate } from '../../../components/templates/MenuHeaderTemplate';
import { BasicTableHead, BasicTableHeadProps } from '../../../components/molecules';
import { useForm, FormProvider } from 'react-hook-form';
import { ListCouponPack, useListCouponPack } from '../hooks/useListCouponPack';
import { HeaderProps } from '../../../components/organisms/general/MenuHeader';
import { TextFieldController } from '../../../components/organisms/form/controllers/TextFieldController';
import { SelectController } from '../../../components/organisms/form/controllers/SelectController';
import { Search } from '@material-ui/icons';
import { ReadMoreButton } from '../../../components/organisms/general/ReadMoreButton';
import { OrganizationContext } from '../../../components/contexts/organization';

const useStyles = makeStyles((theme: Theme) => ({
  conditionFormControl: {
    minWidth: 150,
    maxWidth: 300,
  },
  searchForm: {
    marginTop: theme.spacing(1),
  },
  searchButton: {
    textAlign: 'center',
  },
  couponMastersList: {
    marginTop: theme.spacing(3),
  },
  btnSearch: {
    marginTop: theme.spacing(2),
  },
}));

export type CouponMasterListConditionFormValues = {
  value: string;
  searchCondition: string;
};

type ListCouponPackPageProps = {
  header: HeaderProps;
  listBody: ListCouponPack;
};

export const ListCouponPackBody: React.FC<ListCouponPack> = (props) => {
  const styles = useStyles();
  const history = useHistory();
  const orgContext = useContext(OrganizationContext);

  const couponMasterListForm = useForm<CouponMasterListConditionFormValues>({
    defaultValues: { value: '', searchCondition: 'none' },
  });
  const { handleSubmit, watch } = couponMasterListForm;

  const watchSearchCondition = watch('searchCondition', '');

  const theme = useTheme();
  const tableHeadProps: BasicTableHeadProps = {
    columns: [
      {
        name: 'couponCode',
        label: '回数券コード',
        minWidth: theme.spacing(30),
        align: 'left',
      },
      {
        name: 'couponName',
        label: '回数券名',
        minWidth: theme.spacing(30),
        align: 'left',
      },
      {
        name: 'serialNo',
        label: '回数券ID',
        minWidth: theme.spacing(20),
        align: 'left',
      },
      {
        label: '回数券URL',
        name: 'couponPackUrl',
        minWidth: theme.spacing(30),
        align: 'left',
      },
      {
        name: 'dateExpired',
        label: '有効期限',
        minWidth: theme.spacing(20),
        align: 'left',
      },
      {
        name: 'detailButton',
        label: '',
        minWidth: theme.spacing(20),
        align: 'center',
      },
    ],
  };

  // 検索フォームを条件によって変える
  const searchForm = (): React.ReactElement => {
    switch (watchSearchCondition) {
      case 'serialNo':
        return (
          <Box width={'300px'}>
            <TextFieldController
              name="value"
              label="回数券IDが"
              variant="standard"
              rule={{
                required: {
                  value: true,
                  message: '条件を指定する場合、必須です',
                },
                maxLength: {
                  value: 16,
                  message: '16文字以内で入力してください',
                },
              }}
            />
            <Grid item xs={12} className={styles.btnSearch}>
              <Button
                type="submit"
                variant="outlined"
                size="large"
                color={'default'}
                disabled={props.loading}
                endIcon={<Search />}
                fullWidth
              >
                検索
              </Button>
            </Grid>
          </Box>
        );
      case 'couponPackUrl':
        return (
          <Box width={'300px'}>
            <TextFieldController
              name="value"
              label="回数券URLが"
              variant="standard"
              rule={{
                required: {
                  value: true,
                  message: '条件を指定する場合、必須です',
                },
                maxLength: {
                  value: 200,
                  message: '200文字以内で入力してください',
                },
              }}
            />
            <Grid item xs={12} className={styles.btnSearch}>
              <Button
                type="submit"
                variant="outlined"
                size="large"
                color={'default'}
                disabled={props.loading}
                endIcon={<Search />}
                fullWidth
              >
                検索
              </Button>
            </Grid>
          </Box>
        );
      default:
        // and none
        return (
          <Box width={'300px'} className={styles.btnSearch}>
            <Button
              type="submit"
              variant="outlined"
              size="large"
              color={'default'}
              disabled={props.loading}
              endIcon={<Search />}
              fullWidth
            >
              検索
            </Button>
          </Box>
        );
    }
  };

  // 組織情報が取得できていないタイミングでは、ローディングを出す
  if (orgContext.loading || !orgContext.organization) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  // 組織情報の取得ができているならば、本体を描画する
  return (
    <>
      <Grid container direction="row" justifyContent="space-between" alignItems="center">
        <Grid item>
          <Breadcrumbs>
            <Typography variant="body1">{orgContext.organization.name}の発行した回数券一覧</Typography>
          </Breadcrumbs>
        </Grid>
      </Grid>
      <Box className={styles.searchForm}>
        <FormProvider {...couponMasterListForm}>
          <form onSubmit={handleSubmit(props.onSearchCouponPacks)} autoComplete="off" noValidate>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl className={styles.conditionFormControl}>
                  <SelectController
                    name="searchCondition"
                    label="検索条件"
                    disabled={props.loading}
                    variant="standard"
                    menuItems={[
                      { value: 'none', label: 'なし' },
                      { value: 'serialNo', label: '回数券ID' },
                      { value: 'couponPackUrl', label: '回数券URL' },
                    ]}
                  />
                </FormControl>
              </Grid>
            </Grid>
            {searchForm()}
          </form>
        </FormProvider>
      </Box>
      <Box className={styles.couponMastersList}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TableContainer component={Paper}>
              {props.loading ? <LinearProgress /> : <React.Fragment />}
              <Table>
                <BasicTableHead columns={tableHeadProps.columns} />
                <TableBody>
                  {(() => {
                    if (!props.data || !props.data.couponPacks) {
                      return;
                    } else if (props.data.couponPacks.nodes && props.data.couponPacks.nodes.length === 0) {
                      return (
                        <TableRow>
                          <TableCell>データがありません</TableCell>
                        </TableRow>
                      );
                    } else {
                      return props.data.couponPacks.nodes.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell
                            align="left"
                            component="th"
                            scope="row"
                            style={{
                              wordBreak: 'break-all',
                            }}
                          >
                            {row.couponCode}
                          </TableCell>
                          <TableCell
                            align="left"
                            component="th"
                            scope="row"
                            style={{
                              wordBreak: 'break-all',
                            }}
                          >
                            {row.couponName}
                          </TableCell>
                          <TableCell align="left" component="th" scope="row">
                            {row.serialNo}
                          </TableCell>
                          <TableCell align="left" component="th" scope="row">
                            {row.couponPackUrl}
                          </TableCell>
                          <TableCell align="left" component="th" scope="row">
                            {row.validityEndDateTime}
                          </TableCell>
                          <TableCell align="center" component="th" scope="row">
                            <Button
                              type="button"
                              size="large"
                              color="primary"
                              onClick={() =>
                                history.push({
                                  pathname: `/coupon-pack-masters/${row.couponPackMasterId}/coupon-pack/${row.id}`,
                                  search: history.location.search,
                                })
                              }
                            >
                              詳細
                            </Button>
                          </TableCell>
                        </TableRow>
                      ));
                    }
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          {props.data?.couponPacks.pageInfo.nextToken ? (
            <ReadMoreButton fetchMore={props.onFetchMore} loading={props.loading} />
          ) : (
            <></>
          )}
        </Grid>
      </Box>
    </>
  );
};

const useListCouponPackPage = (): ListCouponPackPageProps => {
  const couponPacksList = useListCouponPack();
  return {
    header: { title: '発行回数券一覧' },
    listBody: couponPacksList,
  };
};

export const ListCouponPackPage: React.FC = () => {
  const listCouponPackPage = useListCouponPackPage();
  return (
    <MenuHeaderTemplate header={listCouponPackPage.header}>
      <Container maxWidth="xl">
        <Box>
          <ListCouponPackBody {...listCouponPackPage.listBody} />
        </Box>
      </Container>
    </MenuHeaderTemplate>
  );
};
